import React, { useEffect, useMemo, useState } from 'react';
import { CheckIcon, ChevronDownIcon, CircleDollarSignIcon, Clock3Icon, InfoIcon, LayersIcon, LockKeyholeIcon, Settings2Icon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { GeographySelector } from './GeographySelector';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { eddyIncomingBatches, onePeopleIncomingBatches, submissionId } from '../data/inventorySubmissions';
type Channel = {
  id: string;
  name: string;
  iconUrl?: string;
};
const channels: Channel[] = [{
  id: 'manymoons',
  name: 'Manymoons',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg'
}, {
  id: 'depop',
  name: 'Depop',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/fG9XmMcsDQikNiC1uba9MJ/dopop.svg'
}, {
  id: 'ebay',
  name: 'eBay',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/7ctVShw3HBQcunanqiLN75/ebay.svg'
}, {
  id: 'mercari',
  name: 'Mercari',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/cisCD3tYpjAhP5v7GoDZxD/mercari.svg'
}, {
  id: 'poshmark',
  name: 'Poshmark',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/n5jkPZQbazKpxu5cMnVHR4/poshmark.svg'
}, {
  id: 'prive',
  name: 'Privé',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/fHmVmW1X2zeRckQHVqfLBY/prive.svg'
}, {
  id: 'tiktok',
  name: 'TikTok Live',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/9zCU7ZyrDgBa4HCXG66eUn/tiktok.svg'
}, {
  id: 'vestiaire',
  name: 'Vestiaire',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/tLawC51vDdyw6q2RLyZ5Bc/vestiaire.svg'
}, {
  id: 'vinted',
  name: 'Vinted',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/6n8bfmq2ixHXbjbeTaLCJz/vinted.svg'
}, {
  id: 'whatnot',
  name: 'Whatnot',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/tKhY3ZxiXEuMX34dV7VA57/whatnot.svg'
}];
type SubmissionSettings = {
  channels: string[];
  indexPrice: number;
  indexAgreement: number;
  indexLocked: boolean;
  timeHorizon: number;
};
const defaultSettings: SubmissionSettings = {
  channels: ['manymoons', 'ebay', 'whatnot'],
  indexPrice: 50,
  indexAgreement: 50,
  indexLocked: false,
  timeHorizon: 6
};
function recoveryCents(months: number) {
  if (months <= 2) return 10;
  if (months <= 6) return 10 + (months - 2) * 30 / 4;
  return 40 + (months - 6) * 20 / 6;
}
export function ControlsDashboard({
  selectedOwner = ''
}: {
  selectedOwner?: string;
}) {
  const allBrands = selectedOwner === '';
  const includes = (brand: string) => allBrands || selectedOwner === brand;
  const {
    data: nisoloSubmissions
  } = useNisoloSubmissions(includes('Nisolo'));
  const submissions = useMemo(() => {
    const rows: {
      id: string;
      brand: string;
      name: string;
      arrived: string;
      units: number;
    }[] = [];
    const push = (brand: string, batches: {
      arrived: string;
      name: string;
      received: number;
    }[]) => {
      batches.forEach((batch, index) => rows.push({
        id: submissionId(brand, index, batch.name),
        brand,
        name: batch.name,
        arrived: batch.arrived,
        units: batch.received
      }));
    };
    if (includes('1 People')) push('1 People', onePeopleIncomingBatches);
    if (includes('Eddy')) push('Eddy', eddyIncomingBatches);
    if (includes('Nisolo')) push('Nisolo', nisoloSubmissions?.submissions ?? []);
    return rows;
  }, [selectedOwner, nisoloSubmissions]);
  const [scopeId, setScopeId] = useState<string>('');
  const [scopeOpen, setScopeOpen] = useState(false);
  // Fall back to the first submission in view whenever the current one disappears.
  useEffect(() => {
    setScopeId(current => submissions.some(submission => submission.id === current) ? current : submissions[0]?.id ?? '');
  }, [submissions]);
  const activeSubmission = submissions.find(submission => submission.id === scopeId) ?? null;

  // Every submission keeps its own rules; switching the selector swaps them out.
  const [rulesById, setRulesById] = useState<Record<string, SubmissionSettings>>({});
  const settings = rulesById[scopeId] ?? defaultSettings;
  const updateSettings = (patch: Partial<SubmissionSettings>) => {
    if (!scopeId) return;
    setRulesById(current => ({
      ...current,
      [scopeId]: {
        ...(current[scopeId] ?? defaultSettings),
        ...patch
      }
    }));
  };
  const {
    channels: activeChannels,
    indexPrice,
    indexAgreement,
    indexLocked,
    timeHorizon
  } = settings;
  const [infoChannel, setInfoChannel] = useState<Channel | null>(null);
  const projectedCents = useMemo(() => Math.round(recoveryCents(timeHorizon)), [timeHorizon]);
  const toggleChannel = (channelId: string) => {
    updateSettings({
      channels: activeChannels.includes(channelId) ? activeChannels.filter(id => id !== channelId) : [...activeChannels, channelId]
    });
  };
  const updateIndexAgreement = (value: number) => {
    const nextValue = Math.max(0, Math.min(100, value));
    updateSettings({
      indexAgreement: nextValue,
      indexPrice: indexLocked ? Math.min(indexPrice, nextValue) : indexPrice
    });
  };
  const setIndexAgreementLock = (locked: boolean) => {
    updateSettings({
      indexLocked: locked,
      indexPrice: locked ? Math.min(indexPrice, indexAgreement) : indexPrice
    });
  };
  const setIndexPrice = (value: number) => updateSettings({
    indexPrice: value
  });
  const setTimeHorizon = (value: number) => updateSettings({
    timeHorizon: value
  });
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="controls-heading" data-id="element-359" data-mp="3abvdxqxpvdy6">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-360" data-mp="3f8lqw5e85tfd">
        <div data-id="element-361" data-mp="2hnzewfkod9y8">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-362" data-mp="2hohlo64yr6ke">
            <Settings2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-363" data-mp="2rkkkp34iqp34" />
            Controls
          </span>
          <h1 id="controls-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-364" data-mp="2vmfnkc23laj0">
            Set your recovery rules.
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-365" data-mp="6wg2qmdlqybg">
            Choose which submissions these rules cover, where that inventory can be sold, and the price guardrails Carli should use.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-366" data-mp="22w6s6uvyn7ek">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-367" data-mp="2guwzt4geoph6" />
          Changes save automatically
        </span>
      </section>

      <section className="mt-8 rounded-3xl border border-[#BB5E40]/25 bg-[#BB5E40]/[0.05] p-5 sm:p-6" aria-labelledby="scope-heading" data-id="element-368" data-mp="1o82giavbrw5a">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-369" data-mp="1k81rxxeii4ki">
          <div data-id="element-370" data-mp="1xz6ub799layj">
            <div className="flex items-center gap-2" data-id="element-371" data-mp="1xzq1pu1aggyb">
              <LayersIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-372" data-mp="1t6wyjalgxgoc" />
              <h2 id="scope-heading" className="text-lg font-semibold" data-id="element-373" data-mp="15huk0bm4s3y2">Applies to</h2>
            </div>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-ink/60" data-id="element-374" data-mp="10ircmkiohlmx">
              {activeSubmission ? `Rules below apply to ${activeSubmission.name} only — ${activeSubmission.units.toLocaleString('en-US')} units uploaded ${activeSubmission.arrived}. Each submission keeps its own rules.` : 'Upload inventory to set rules for a specific submission.'}
            </p>
          </div>

          <div className="relative w-full lg:w-80" data-id="element-375" data-mp="383q557jwi1wu">
            <button type="button" onClick={() => setScopeOpen(open => !open)} aria-expanded={scopeOpen} aria-haspopup="listbox" disabled={submissions.length === 0} className="flex w-full items-center justify-between gap-3 rounded-xl border border-ink/15 bg-paper px-4 py-3 text-left text-sm font-semibold transition-colors hover:border-[#BB5E40]/45 disabled:opacity-50" data-id="element-376" data-mp="382f3bda0n48d">
              <span className="truncate" data-id="element-377" data-mp="2afzgdvytledf">{activeSubmission?.name ?? 'No submissions yet'}</span>
              <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform ${scopeOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-378" data-mp="2fgn7ehn1tv7n" />
            </button>

            {scopeOpen && <div role="listbox" aria-label="Inventory submissions" className="absolute right-0 z-20 mt-2 w-full rounded-xl border border-ink/10 bg-paper p-1.5 shadow-lg lg:w-96" data-id="element-379" data-mp="323j47ok8pvgd">
                <span className="block px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-380" data-mp="2s6xskkdptm3k">Choose one submission</span>
                <div className="max-h-72 overflow-y-auto" data-id="element-381" data-mp="3pt53ec0m4aod">
                  {submissions.map(submission => {
                const selected = submission.id === scopeId;
                const customized = Boolean(rulesById[submission.id]);
                return <button key={submission.id} type="button" role="option" aria-selected={selected} onClick={() => {
                  setScopeId(submission.id);
                  setScopeOpen(false);
                }} className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors ${selected ? 'bg-[#BB5E40]/10' : 'hover:bg-cream/70'}`} data-id="element-382" data-mp="3b4h7g4mutyxw">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center" data-id="element-383" data-mp="tx8izzvzgsks">
                          {selected && <CheckIcon className="h-3.5 w-3.5 text-[#BB5E40]" aria-hidden="true" data-id="element-384" data-mp="ywlgdekhdi1m" />}
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-385" data-mp="12vu86dvyn5uj">
                          <span className="block truncate text-sm font-medium" data-id="element-386" data-mp="17nlq6011w392">{submission.name}</span>
                          <span className="block text-xs text-ink/50" data-id="element-387" data-mp="1cnq71r5vezzt">
                            {allBrands ? `${submission.brand} · ` : ''}{submission.arrived} · {submission.units.toLocaleString('en-US')} units
                          </span>
                        </span>
                        {customized && <span className="shrink-0 rounded-full bg-[#BB5E40]/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-388" data-mp="4or97mu8ozd2">Custom</span>}
                      </button>;
              })}
                </div>
              </div>}
          </div>
        </div>
      </section>

      <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="channels-heading" data-id="element-389" data-mp="ignylj4iwv5r">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-390" data-mp="nhbpm4sr5bzz">
          <div data-id="element-391" data-mp="sg6ijzkfogxt">
            <div className="flex items-center gap-2" data-id="element-392" data-mp="wfnoeu3rvg3i">
              <SlidersHorizontalIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-393" data-mp="wg6wlj3meq4c" />
              <h2 id="channels-heading" className="text-lg font-semibold" data-id="element-394" data-mp="11dgw5rkq4i5w">Approved channels</h2>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-ink/60" data-id="element-395" data-mp="2y8lewja2twla">Select the marketplaces where this inventory is eligible to appear.</p>
          </div>
          <span className="text-sm font-medium text-ink/55" data-id="element-396" data-mp="2vn45m7cdcqbi">{activeChannels.length} selected</span>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-397" data-mp="2qvm159b507ls">
          {channels.map(channel => {
          const selected = activeChannels.includes(channel.id);
          const infoOpen = infoChannel?.id === channel.id;
          return <div key={channel.id} className="relative" data-id="element-398" data-mp="3elgfjvb3u485">
                <button type="button" onClick={() => toggleChannel(channel.id)} aria-pressed={selected} className={`flex min-h-[76px] w-full items-center gap-2.5 rounded-xl border p-3 pr-7 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20 ${selected ? 'border-[#BB5E40] bg-[#BB5E40] text-cream shadow-sm' : 'border-ink/10 bg-cream/40 hover:border-[#BB5E40]/30'}`} data-id="element-399" data-mp="3ekyac96gsfo3">
                  <ChannelBadge channel={channel} selected={selected} data-id="element-400" data-mp="1p8whl5y1h3on" />
                  <span className={`min-w-0 flex-1 whitespace-normal break-words text-[13px] font-semibold leading-tight sm:text-sm ${selected ? 'text-cream' : 'text-ink'}`} data-id="element-401" data-mp="1qo8nmyzpk3w5">{channel.name}</span>
                </button>
                <button type="button" onClick={() => setInfoChannel(infoOpen ? null : channel)} aria-label={`About ${channel.name}`} aria-expanded={infoOpen} className={`absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20 ${selected ? 'text-cream/75 hover:bg-cream/15 hover:text-cream' : 'text-ink/45 hover:bg-ink/5 hover:text-[#BB5E40]'}`} data-id="element-402" data-mp="1grm3ydj4bivp">
                  <InfoIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-403" data-mp="oz55wdwmv7n8" />
                </button>
                {infoOpen && <div role="dialog" aria-label={`About ${channel.name}`} className="absolute left-0 top-[calc(100%+8px)] z-20 w-64 rounded-xl border border-ink/10 bg-paper p-4 text-left shadow-lg" data-id="element-404" data-mp="abn0fdb8y8zr">
                    <div className="flex items-start justify-between gap-3" data-id="element-405" data-mp="jcbeu94jybd6">
                      <div data-id="element-406" data-mp="ja8f2xdll3v9">
                        <p className="text-sm font-semibold text-ink" data-id="element-407" data-mp="edhx24i8fy77">{channel.name}</p>
                        <p className="mt-1 text-xs leading-relaxed text-ink/60" data-id="element-408" data-mp="taisoj3joday">Channel description coming soon. Add the approved positioning, audience, and merchandising notes for this marketplace here.</p>
                      </div>
                      <button type="button" onClick={() => setInfoChannel(null)} className="-mr-1 -mt-1 rounded-md p-1 text-ink/45 transition-colors hover:bg-ink/5 hover:text-ink" aria-label={`Close ${channel.name} details`} data-id="element-409" data-mp="1232lv412hpoe">
                        <XIcon className="h-3.5 w-3.5" data-id="element-410" data-mp="16zszlkpeg96n" />
                      </button>
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" data-id="element-411" data-mp="1zwlotkry9ru3">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 sm:p-7" aria-labelledby="pricing-heading" data-id="element-412" data-mp="1zyew92ae0g35">
          <div className="flex items-center gap-2" data-id="element-413" data-mp="1855u13g8oyss">
            <CircleDollarSignIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-414" data-mp="186h57oc0l8tx" />
            <h2 id="pricing-heading" className="text-lg font-semibold" data-id="element-415" data-mp="1c7srpkzlddxw">Pricing guardrails</h2>
          </div>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/60" data-id="element-416" data-mp="1h52jh7age1ow">This setting guides how your inventory is publicly merchandised.</p>

          <div className="mt-7 space-y-7" data-id="element-417" data-mp="2skzmu0j1go5n">
            <PricingSlider label="Index price" value={indexPrice} onChange={setIndexPrice} helper="The public price displayed online—on Manymoons, marketplaces, and search listings—before any customer discount codes are applied." min={0} max={indexLocked ? indexAgreement : 100} agreement={indexAgreement} onAgreementChange={updateIndexAgreement} locked={indexLocked} onLockChange={setIndexAgreementLock} data-id="element-418" data-mp="2sk7r8ppfyqfd" />
          </div>
        </article>

        <article className="rounded-3xl border border-ink/10 bg-forest p-5 text-cream sm:p-7" aria-labelledby="horizon-heading" data-id="element-419" data-mp="37aiytdnh7cs">
          <div className="flex items-center gap-2 text-cream/80" data-id="element-420" data-mp="3ub90w9161rph">
            <Clock3Icon className="h-4 w-4" aria-hidden="true" data-id="element-421" data-mp="3peil7mt9wy4l" />
            <h2 id="horizon-heading" className="text-lg font-semibold text-cream" data-id="element-422" data-mp="3kee53xwa25ew">Sales horizon</h2>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-cream/70" data-id="element-423" data-mp="1nmm8waza7k8r">More time lets Carli preserve value with less aggressive pricing.</p>

          <div className="mt-7 rounded-2xl border border-cream/15 bg-cream/[0.08] p-4" data-id="element-424" data-mp="1inj50tv1fk2d">
            <div className="flex items-end justify-between gap-3" data-id="element-425" data-mp="3d2g6b3hh5n2e">
              <span className="text-sm font-medium text-cream/75" data-id="element-426" data-mp="3hugd9fvypg8h">Time to sell</span>
              <span className="font-display text-3xl font-semibold tracking-tight" data-id="element-427" data-mp="3351yttdj48m8">{timeHorizon} months</span>
            </div>
            <input type="range" min={2} max={12} step={1} value={timeHorizon} onChange={event => setTimeHorizon(Number(event.target.value))} style={{
            '--horizon-progress': `${(timeHorizon - 2) / 10 * 100}%`
          } as React.CSSProperties} className="controls-horizon-slider mt-5 w-full" aria-label="Sales horizon in months" data-id="element-428" data-mp="383de4jmlfbbu" />
            <div className="mt-1 flex justify-between text-xs text-cream/55" data-id="element-429" data-mp="3qwh5tzg93e3t">
              <span data-id="element-430" data-mp="2bckwhhk0hb7y">2 months</span>
              <span data-id="element-431" data-mp="2ff7s7tjszgaf">12 months</span>
            </div>
          </div>

          <div className="mt-5 border-t border-cream/15 pt-5" data-id="element-432" data-mp="2fdd4y8l27c36">
            <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-433" data-mp="27und3uyajzrm">Projected recovery</p>
            <p className="mt-2 font-display text-4xl font-semibold tracking-tight" data-id="element-434" data-mp="22wc3nlbwrr9x">{projectedCents}¢ <span className="text-lg font-medium text-cream/65" data-id="element-435" data-mp="2hstdu98d2zuw">per $1 MSRP</span></p>
            <p className="mt-2 text-sm leading-relaxed text-cream/70" data-id="element-436" data-mp="2curvxrmo96l0">At this horizon, Carli will aim to recover approximately {projectedCents} cents for every MSRP dollar while maintaining your pricing guardrails.</p>
          </div>
        </article>
      </section>

      <GeographySelector data-id="element-437" data-mp="1yva39169996d" />
    </div>;
}
function ChannelBadge({
  channel,
  selected
}: {
  channel: Channel;
  selected: boolean;
}) {
  return <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg p-2 ${selected ? 'bg-cream/15' : 'bg-[#BB5E40]'}`} data-id="element-438" data-mp="1u3ilng52udsd"><img src={channel.iconUrl} alt="" className={`h-full w-full object-contain ${channel.id === 'ebay' ? 'scale-125' : ''}`} data-id="element-439" data-mp="16d4rnk2xm4v5" /></span>;
}
function PricingSlider({
  label,
  value,
  onChange,
  helper,
  min,
  max,
  agreement,
  onAgreementChange,
  locked,
  onLockChange
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  helper: string;
  min: number;
  max: number;
  agreement: number;
  onAgreementChange: (value: number) => void;
  locked: boolean;
  onLockChange: (locked: boolean) => void;
}) {
  const agreementId = `${label.toLowerCase().replace(' ', '-')}-agreement`;
  const lockId = `${label.toLowerCase().replace(' ', '-')}-lock`;
  return <div data-id="element-440" data-mp="13t7fyz7moga2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-441" data-mp="13sxnn4z41x17">
        <label className="pt-1 text-sm font-semibold text-ink" data-id="element-442" data-mp="18mjps37neiir">{label}</label>
        <div className="flex items-center gap-2 self-start rounded-xl border border-ink/10 bg-cream/65 p-1.5" data-id="element-443" data-mp="1dkl98p8zkjur">
          <label htmlFor={agreementId} className="sr-only" data-id="element-444" data-mp="1hmydtabzybpy">Agreed {label.toLowerCase()} ceiling</label>
          <input id={agreementId} type="number" min={min} max={100} value={agreement} onChange={event => onAgreementChange(Number(event.target.value))} className="w-12 bg-transparent text-right text-sm font-semibold tabular-nums text-ink focus:outline-none" data-id="element-445" data-mp="1mlrtn9eraeta" />
          <span className="text-xs font-medium text-ink/50" data-id="element-446" data-mp="2t39hif24l5qt">% agreed</span>
          <span className="h-5 w-px bg-ink/10" aria-hidden="true" data-id="element-447" data-mp="2t5cg3nhcg775" />
          <input id={lockId} type="checkbox" checked={locked} onChange={event => onLockChange(event.target.checked)} className="h-4 w-4 rounded border-ink/25 accent-[#BB5E40]" data-id="element-448" data-mp="3ps8ozruvy3hy" />
          <label htmlFor={lockId} className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-ink/65" data-id="element-449" data-mp="3c259djqih81h">
            <LockKeyholeIcon className={`h-3.5 w-3.5 ${locked ? 'text-[#BB5E40]' : 'text-ink/40'}`} aria-hidden="true" data-id="element-450" data-mp="dcn9ns0yq08q" />
            Lock
          </label>
        </div>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-ink/55" data-id="element-451" data-mp="q8nejcq63j5w">
        <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-452" data-mp="q6apygpwmajv" />
        <p data-id="element-453" data-mp="lbwxon6jgjjf">{helper}</p>
      </div>
      <div className="mt-3 flex items-baseline justify-between gap-3" data-id="element-454" data-mp="g9xx1l26o0q6">
        <span className="text-xs font-medium uppercase tracking-wider text-ink/45" data-id="element-455" data-mp="bbe7laods6t2">Current setting</span>
        <span className="font-display text-2xl font-semibold tabular-nums text-[#BB5E40]" data-id="element-456" data-mp="18z510zkepiit">{value}% <span className="font-sans text-xs font-medium text-ink/45" data-id="element-457" data-mp="190zho1onaoh3">of MSRP</span></span>
      </div>
      <input type="range" min={min} max={max} step={1} value={value} onChange={event => onChange(Number(event.target.value))} className="mt-3 w-full accent-[#BB5E40]" aria-label={`${label} percentage of MSRP`} data-id="element-458" data-mp="z9bsy8jh3ljb" />
      <div className="mt-1 flex justify-between text-xs text-ink/45" data-id="element-459" data-mp="1rrpizlyki37u">
        <span data-id="element-460" data-mp="1my4i9m5lav4y">{min}%</span>
        <span data-id="element-461" data-mp="1mwtevng23ped">{max}%{locked ? ' agreement ceiling' : ''}</span>
      </div>
    </div>;
}